import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { createStaticTranslateKey, StaticTranslateKey } from '../lib/create-static-translate-key';
import { randomString } from '../lib/random-string';
import { AppExtraLoadingService } from './app-extra-loading.service';
import { TranslateService } from './translate.service';

type ToastPreset = 'error' | 'warning' | 'success' | 'info' | 'custom';
type ToastColor = 'primary' | 'dark' | 'black' | 'gray' | 'green' | 'warn' | 'orange' | 'green';

export interface Toast {
  id: string;
  size: 'tiny' | 'small' | 'medium' | 'large' | 'auto';
  title: string;
  message: string;
  icon: string;
  image: string;
  customSvg?: string;
  imageSize: number;
  backgroundImage: string;
  backgroundImageSize: number;
  color: ToastColor;
  duration: number;
  fontSize?: number;
  showLoader?: boolean;
  titleFontSize?: number;
  closable: boolean;
  vertical: boolean;
  cta?: {
    color: string;
    text?: string;
    textKey?: StaticTranslateKey;
    icon?: string;
    height?: string;
    size?: string;
    maxWidth?: string;
    onClick: () => void;
  };
  onClose?: () => void;
  location: 'head' | 'footer' | 'bottom-left' | 'bottom-right';
}

const defaultToastOptions: Toast = {
  id: undefined,
  closable: false,
  duration: 2500,
  size: 'small',
  icon: undefined,
  image: undefined,
  imageSize: 64,
  backgroundImage: undefined,
  backgroundImageSize: 128,
  color: 'black',
  title: undefined,
  message: undefined,
  location: 'head',
  vertical: false,
};

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private appExtraLoadingService = inject(AppExtraLoadingService);
  private translateService = inject(TranslateService);
  private toasts: Toast[] = [];
  toasts$ = new BehaviorSubject<Toast[]>(this.toasts);
  presets = {
    error: { icon: '/assets/icons/cross-circle.svg', color: 'warn' },
    warning: { icon: '/assets/icons/alert-circle.svg', color: 'orange' },
    success: { icon: '/assets/icons/check-circle.svg', color: 'green' },
    info: { icon: '/assets/icons/info-circle.svg', color: 'primary' },
  };

  private preAdd(preset: ToastPreset, options: Partial<Toast> = {}) {
    this.appExtraLoadingService.load$.next('toasts');
    const defaultOptions = { ...defaultToastOptions };
    if (options.closable && !options.duration) defaultOptions.duration = undefined;
    if (preset && this.presets[preset]) {
      defaultOptions.icon = this.presets[preset].icon;
      defaultOptions.color = this.presets[preset].color;
    }
    return defaultOptions;
  }

  private postAdd(newToast: Toast) {
    this.toasts.push(newToast);
    this.toasts$.next(this.toasts);
    if (newToast.duration !== undefined) {
      setTimeout(() => {
        const index = this.toasts.indexOf(newToast);
        if (index > -1) {
          this.toasts.splice(index, 1);
          this.toasts$.next(this.toasts);
        }
      }, newToast.duration);
    }
  }

  addFromObservable(observable: Observable<string>, preset: ToastPreset, options: Partial<Toast> = {}) {
    const defaultOptions = this.preAdd(preset, options);
    const newToast: Toast = { ...defaultOptions, ...options, id: randomString(12) };
    observable.subscribe(translatedMessage => {
      newToast.message = translatedMessage;
      this.postAdd(newToast);
    });
    return newToast;
  }

  add(message: StaticTranslateKey, preset: ToastPreset, options: Partial<Toast> = {}) {
    const defaultOptions = this.preAdd(preset, options);
    const newToast: Toast = { ...defaultOptions, ...options, id: randomString(12) };
    this.translateService.translate(message).subscribe(translatedMessage => {
      newToast.message = translatedMessage;
      this.postAdd(newToast);
    });
    return newToast;
  }

  addPreTranslated(message: string, preset: ToastPreset, options: Partial<Toast> = {}) {
    const defaultOptions = this.preAdd(preset, options);
    const newToast: Toast = { ...defaultOptions, ...options, id: randomString(12) };
    newToast.message = message;
    this.postAdd(newToast);
    return newToast;
  }

  addCommon(messageKey: 'link-copied' | 'copied' | 'invalid-file-format') {
    if (messageKey === 'link-copied') {
      this.add(createStaticTranslateKey('Link copied!', 'common-toasts.link-copied'), 'success');
    }
    if (messageKey === 'copied') {
      this.add(createStaticTranslateKey('Copied to clipboard', 'common-toasts.copied'), 'success');
    }
    if (messageKey === 'invalid-file-format') {
      this.add(createStaticTranslateKey('Invalid file format', 'common-toasts.invalid-file-format'), 'error');
    }
  }

  public close(toast: Toast) {
    if (toast.onClose) toast.onClose();
    const index = this.toasts.indexOf(toast);
    if (index > -1) this.toasts.splice(index, 1);
    this.toasts$.next(this.toasts);
  }
}
